import { ActionsObservable, combineEpics, StateObservable } from 'redux-observable'
import { array } from 'fp-ts'
import { Deps, State } from 'src/app.store'
import { DSService } from 'src/services/ds.service'
import { filter, mergeMap } from 'rxjs/operators'
import { from, Observable } from 'rxjs'
import { Future } from 'funfix'
import { LoadTest, SaveTest, SendInvites, TestPageActions, testPageActions } from './test-page.actions'
import { startFetch, stopFetch } from 'src/framework/fetch-reducer'
import { TestWithAnswers } from './test-page.reducer'
import { genericError } from 'src/utils/notifications.utils'
import * as O from 'fp-ts/lib/Option'
import { pipe } from 'fp-ts/lib/pipeable'

interface Action$ extends ActionsObservable<TestPageActions> {}
interface State$ extends StateObservable<State> {}

const loadTest = (dsService: DSService) => (p: LoadTest): Observable<TestPageActions> =>
  from(
    Future.map2(
      dsService.getTest({ language: 'fr', id: p.token }),
      dsService.getTestResults({ token: p.token }),
      (testResultOpt, answersResultOpt) =>
        pipe(
          testResultOpt,
          O.chain(testResult =>
            pipe(
              answersResultOpt,
              O.map(answersResult => ({
                testResult,
                answersResult
              }))
            )
          ),
          O.fold(
            () => testPageActions.LoadTestFailed({ [stopFetch]: p[startFetch] }),
            ({ testResult, answersResult }) => {
              const twa: TestWithAnswers = {
                content: testResult.result,
                token: p.token,
                latest: false,
                answers: array.zipWith(
                  testResult.result.test,
                  answersResult.result.answersIds,
                  (question, answerId) => ({ id: question.id, result: answerId })
                )
              }
              return testPageActions.LoadTestSuccess({ [stopFetch]: p[startFetch], token: p.token, test: twa })
            }
          )
        )
    )
  )

const loadTestPageEpic = (action$: Action$, _: State$, deps: Deps): Observable<TestPageActions> =>
  action$.pipe(
    filter(testPageActions.is.LoadTest),
    mergeMap(loadTest(deps.dsService))
  )

const saveResults = (dsService: DSService) => (p: SaveTest): Observable<TestPageActions> =>
  from(
    dsService.sendTestResult(p).map(res =>
      pipe(
        res,
        O.fold(
          () => {
            genericError()
            return testPageActions.SaveTestFailure({ [stopFetch]: p[startFetch] })
          },
          _res =>
            testPageActions.SaveTestSuccess({
              [stopFetch]: p[startFetch],
              token: p.token,
              answers: p.answers,
              latest: p.latest
            })
        )
      )
    )
  )

const saveTestPageEpic = (action$: Action$, _: State$, deps: Deps): Observable<TestPageActions> =>
  action$.pipe(
    filter(testPageActions.is.SaveTest),
    mergeMap(saveResults(deps.dsService))
  )

const sendInvites = (dsService: DSService) => (p: SendInvites): Observable<TestPageActions> =>
  from(
    dsService.inviteUsersToSubTest({ id: p.token, invites: p.invites }).map(res =>
      pipe(
        res,
        O.fold(
          () => testPageActions.SendInvitesFailure({ [stopFetch]: p[startFetch] }),
          _res =>
            testPageActions.SendInvitesSuccess({
              [stopFetch]: p[startFetch]
            })
        )
      )
    )
  )

const sendInvitesPageEpic = (action$: Action$, _: State$, deps: Deps): Observable<TestPageActions> =>
  action$.pipe(
    filter(testPageActions.is.SendInvites),
    mergeMap(sendInvites(deps.dsService))
  )

export const testPageEpics = combineEpics(loadTestPageEpic, saveTestPageEpic, sendInvitesPageEpic)



// WEBPACK FOOTER //
// ./src/tests-components/test-page/test-page.epics.ts